import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { transitionWidth } from 'animations-lib';
import { Workflow } from 'enums-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Subscription } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { EntityTypeServiceFactory } from '../../../services/entity-type/entity-type-service.factory';
import { EntityTypeService, IEntityLabelPair } from '../../../services/entity-type/entity-type.service';

@Component({
    selector: 'app-template-availability',
    templateUrl: './template-availability.component.html',
    styleUrls: ['./template-availability.component.css'],
    animations: [transitionWidth],
    standalone: false
})
export class TemplateAvailabilityComponent implements OnInit, OnDestroy {
  @Input() isNewConfig: boolean;
  @Input() workflowType: Workflow;
  @Input() filtersFormArray: UntypedFormArray;
  @Input() configuration: UntypedFormGroup;

  private subscriptions: Subscription = new Subscription();
  private entityTypes: ({ tooltip: string } & IEntityLabelPair)[] = [];
  private static RECORD_ENTITY_NAME_PATH_ARRAY: string[];
  templateHasAvailabilityOptions = false;
  availabilityEntityTypes: ({ tooltip: string } & IEntityLabelPair)[] = [];
  selectedAvailabilityEntityTypes: ({ tooltip: string } & IEntityLabelPair)[] = [];

  constructor(
    private identityStorage: IdentityStorage,
    private entityTypeService: EntityTypeService,
    private entityTypeServiceFactory: EntityTypeServiceFactory,
    private messageNotificationService: MessageNotificationService
  ) {
    TemplateAvailabilityComponent.RECORD_ENTITY_NAME_PATH_ARRAY = [this.identityStorage.get().namespace, 'record', 'entity', 'name'];
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.configuration.get('entity').valueChanges.subscribe((_) => {
        this.updateTemplateAvailability();
      })
    );
    this.getEntityTypes();
  }

  get entitySelectedLabels() {
    return this.selectedAvailabilityEntityTypes.map((x) => x.label);
  }

  get entitySelectedNames() {
    return this.selectedAvailabilityEntityTypes.map((x) => x.objectName);
  }

  private getEntityTypes() {
    this.entityTypeService.getEntitiesForNamespace(this.identityStorage.get().namespace).subscribe({
      next: (entities) => {
        this.entityTypes = this.entityTypeServiceFactory
          .getMappedEntityTypes(this.identityStorage.get().namespace, entities)
          .map((elp) => ({
            ...elp,
            tooltip: this.entityTypeServiceFactory.lookupEntityName(this.identityStorage.get().namespace, elp.objectName),
          }));
        if (!this.isNewConfig) {
          this.updateTemplateAvailability();
        }
      },
      error: (error) => {
        this.messageNotificationService.error(`Failed to get entities for workflow config.`, error);
      },
    });
  }

  private updateTemplateAvailability() {
    const entity = this.configuration.get('entity').value;
    this.templateHasAvailabilityOptions = this.entityTypeServiceFactory.entityTypeHasAvailabilityOptions(
      this.identityStorage.get().namespace,
      entity,
      this.workflowType
    );

    this.availabilityEntityTypes = [];
    if (entity && this.entityTypes?.length > 0) {
      this.availabilityEntityTypes = this.entityTypeServiceFactory.getTemplateAvailabilityEntityTypes(
        this.identityStorage.get().namespace,
        this.entityTypes,
        entity
      );
    }

    if (this.isNewConfig) {
      this.selectedAvailabilityEntityTypes = this.availabilityEntityTypes;
    } else {
      const filterFormGroup = this.findFormGroup(TemplateAvailabilityComponent.RECORD_ENTITY_NAME_PATH_ARRAY);
      this.selectedAvailabilityEntityTypes = filterFormGroup
        ? this.availabilityEntityTypes.filter((x) => filterFormGroup.get('filterValues').value.includes(x.objectName))
        : this.availabilityEntityTypes;
    }
    this.updateFilters();
  }

  availabilityChange(): void {
    this.updateFilters();
    this.filtersFormArray.markAsDirty();
  }

  updateFilters() {
    if (this.templateHasAvailabilityOptions) {
      const filterFormGroup = this.findFormGroup(TemplateAvailabilityComponent.RECORD_ENTITY_NAME_PATH_ARRAY);
      if (filterFormGroup) {
        filterFormGroup.get('filterValues').patchValue(this.entitySelectedNames);
      } else {
        this.addFilter();
      }
    } else {
      this.removeFilter();
    }
  }

  private addFilter() {
    const newFormGroup = new UntypedFormGroup({
      entityPath: new UntypedFormControl(TemplateAvailabilityComponent.RECORD_ENTITY_NAME_PATH_ARRAY),
      filterValues: new UntypedFormControl(this.entitySelectedNames),
      operator: new UntypedFormControl('ANY_MATCH'),
      filterType: new UntypedFormControl('AVAILABILITY'),
    });

    this.filtersFormArray.push(newFormGroup);
  }

  private removeFilter() {
    const index = this.findFormGroupIndex(TemplateAvailabilityComponent.RECORD_ENTITY_NAME_PATH_ARRAY);
    if (index !== -1) {
      this.filtersFormArray.removeAt(index);
    }
  }

  private findFormGroup(findPath: string[]): UntypedFormGroup | undefined {
    const dottedPath = findPath.join('.');

    return this.filtersFormArray.controls.find((control) => {
      const formGroup = control as UntypedFormGroup;
      const fieldPathArray = formGroup.get('entityPath').value as string[];
      return fieldPathArray.join('.') === dottedPath;
    }) as UntypedFormGroup;
  }

  private findFormGroupIndex(findPath: string[]): number {
    const dottedPath = findPath.join('.');

    return this.filtersFormArray.controls.findIndex((control) => {
      const formGroup = control as UntypedFormGroup;
      const fieldPathArray = formGroup.get('entityPath').value as string[];
      return fieldPathArray.join('.') === dottedPath;
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
